import * as THREE from 'three';
import EventDispatcher from '../common/EventDispatcher';
import { Global } from '../common/global';
import * as CANNON from 'cannon';
import gsap from 'gsap';
import { CustomEase } from 'gsap/all';
import Trail from './Trail';
import { isMobile } from 'mobile-device-detect';

const BALL_RADIUS = 0.12688752645712992 * Global.scaleFact * 0.8;

export default class Ball {
  constructor(scene) {
    this.scene = scene;
    this.raycaster = new THREE.Raycaster(); // Initialize the raycaster
    this.previousPosition = new THREE.Vector3(); // Store the previous position of the ball
    this.onEvents = this.onEvents.bind(this);
  }

  init() {
    this.events = EventDispatcher.getObj();

    this.events.addEventListener('GAMESCENE_EVENTS', this.onEvents);
    this.events.addEventListener('PLAYER_EVENTS', this.onEvents);
    this.events.addEventListener('GAME_EVENTS', this.onEvents);

    this.canCheckForGoal = false;
    this.canCheckRaycaster = false;
    this.kickFinished = false;
    this.kickStarted = false;
    this.kickCnt = 0;
    this.ballVelocities = [
      { x: -3.5, y: 6.3, z: 27 }, //High Left
      { x: 2, y: 6.3, z: 27 }, //High Right
      { x: -3.5, y: 5.7, z: 27 }, //Mid Left
      { x: 2, y: 5.7, z: 27 }, //Mid Right
      { x: -3.5, y: 5.3, z: 25 }, //Low Left
      { x: 1.2, y: 5.3, z: 25 }, //Low Right
    ];

    this.velIndex = 5;
    Global.jumpHeight = 'low';
  }

  onEvents(data) {
    switch (data['message']['event_type']) {
      case 'create_scene':
        this.addBall();
        break;
      case 'update':
        this.update(data['message']['data']);
        break;
      case 'launch_ball':
        this.launchBall(data['message']['data']);
        break;
      case 'assign_position':
        this.placeBall(data['message']['data']);
        break;
      case 'dispose':
        this.dispose();
        break;
    }
  }

  addBall() {
    this.ball = Global.assets['glbs'][`ball`]['asset'].scene;
    this.scene.add(this.ball);
    this.ball.scale.set(
      Global.scaleFact * 1.2,
      Global.scaleFact * 1.2,
      Global.scaleFact * 1.2
    );

    Global.assets['textures'][`shadow`]['asset'].colorSpace =
      THREE.SRGBColorSpace;

    this.shadow = new THREE.Mesh(
      new THREE.PlaneGeometry(0.2, 0.1),
      new THREE.MeshBasicMaterial({
        side: THREE.DoubleSide,
        map: Global.assets['textures'][`shadow`]['asset'],
        transparent: true,
        opacity: 0.2,
      })
    );
    this.shadow.scale.set(
      Global.scaleFact * 1.2,
      Global.scaleFact * 1.2,
      Global.scaleFact * 1.2
    );
    this.shadow.rotation.x = Math.PI / 2;
    this.scene.add(this.shadow);

    const ballShape = new CANNON.Sphere(BALL_RADIUS);
    this.ballBody = new CANNON.Body({
      mass: 1,
      shape: ballShape,
      material: Global.ballMaterial,
      position: new CANNON.Vec3(0, 0, 0),
    });
    this.scene.world.addBody(this.ballBody);

    this.ballBody.allowSleep = true;
    this.ballBody.sleepSpeedLimit = 0.1;
    this.ballBody.sleepTimeLimit = 1;

    this.ballBody.linearDamping = 0.2;
    this.ballBody.angularDamping = 0.4;

    this.trail = new Trail(this.ballBody, {
      length: 20,
      size: 1.3 * (isMobile ? 0.75 : 1),
      decay: 0.98,
      color: 0x0000ff,
    });
    this.trail.addToScene(this.scene);

    this.ballBody.addEventListener('collide', (event) => {
      if (!this.canCheckForGoal) return;

      this.canCheckRaycaster = false;

      if (
        event.body.material === Global.netMaterial &&
        this.ballBody.position.z >= -1.2
      ) {
        this.events.dispatchEvent({
          type: 'SOUND_EVENTS',
          message: {
            event_type: 'play_sound',
            data: {
              sndKey: 'net',
            },
          },
        });
        this.events.dispatchEvent({
          type: 'SOUND_EVENTS',
          message: {
            event_type: 'play_sound',
            data: {
              sndKey: 'miss',
            },
          },
        });
        this.kickFinished = true;
        this.canCheckForGoal = false;
        this.isGoal = true;
        this.events.dispatchEvent({
          type: 'CAMERA_EVENTS',
          message: {
            event_type: 'shake_camera',
            data: {
              shake_fact: 0.2,
              shake_time: 0.3,
            },
          },
        });

        this.events.dispatchEvent({
          type: 'GAME_EVENTS',
          message: {
            event_type: 'goal',
            data: {},
          },
        });
      }
      if (event.body.material === Global.netMaterial2) {
        /*    this.kickFinished = true;
        this.canCheckForGoal = false;
        this.isGoal = true; */
        // this.events.dispatchEvent({
        //     type: "CAMERA_EVENTS",
        //     message: {
        //         "event_type": "shake_camera",
        //         data: {
        //             "shake_fact": 1.3,
        //             "shake_time": 0.25
        //         }
        //     }
        // });
      }
      this.stopBallMove(event);
    });

    this.ball.traverse((child) => {
      if (!child.material) return;

      child.material.envMap = Global.envMap;
      child.material.envMapIntensity = 1.0;
      child.material.roughness = 0.5;
    });
  }
  stopBallMove(event) {
    if (this.launchTwn) {
      this.launchTwn && this.launchTwn.kill();
      this.launchTwn = null;

      const contactNormal = event.contact.ni;
      const relativeVelocity = event.contact.getImpactVelocityAlongNormal();
      const forceMagnitude = Math.abs(relativeVelocity) * 10;

      const reactionForce = new CANNON.Vec3(
        contactNormal.x * forceMagnitude,
        contactNormal.y * forceMagnitude + 100,
        contactNormal.z * forceMagnitude
      );

      this.ballBody.applyForce(reactionForce, this.ballBody.position);

      const spinMagnitude = 5;
      // this.ballBody.angularVelocity.set(
      //   contactNormal.y * spinMagnitude,
      //   -contactNormal.x * spinMagnitude,
      //   0
      // );
    }
  }

  placeBall(data) {
    const { x, y, z } = data['position'];

    this.ballBody.angularVelocity.set(0, 0, 0);
    this.ballBody.velocity.set(0, 0, 0);
    this.ballBody.position.set(x, y, z);
    this.kickStarted = false;
    // this.trail.updateTracking(true);

    gsap.delayedCall(0.0, () => {
      this.events.dispatchEvent({
        type: 'PLAYER_EVENTS',
        message: {
          event_type: 'move_camera',
          data: {
            position: new THREE.Vector3(
              this.ball.position.x - 0.75,
              this.ball.position.y + 1.2,
              this.ball.position.z
            ),
            reset: true,
          },
        },
      });
    });
  }
  updateTrail() {
    this.trail.updateTrail();
  }
  update() {
    if (this.ball && this.ballBody) {
      // if (this.ballBody.position.z >= -1.3) {
      //   this.trail.updateTracking(false);
      // }
      // this.trail.update();
      this.ball.position.copy(this.ballBody.position);
      this.ball.quaternion.copy(this.ballBody.quaternion);
      this.shadow.position.set(
        this.ball.position.x,
        0.001 * Global.scaleFact,
        this.ball.position.z
      );
      Global.ballPos = this.ballBody.position.clone();
      // Update the previous position and check for intersections
      this.ball.updateMatrixWorld();

      if (this.canCheckRaycaster) this.checkForIntersections();
    }

    if (this.kickStarted && !this.kickFinished) {
      // this.events.dispatchEvent({
      //   type: 'PLAYER_EVENTS',
      //   message: {
      //     event_type: 'move_camera',
      //     data: {
      //       position: new THREE.Vector3(
      //         this.ball.position.x,
      //         this.ball.position.y - 1,
      //         this.ball.position.z
      //       ),
      //     },
      //   },
      // });
    }
  }

  checkForIntersections() {
    const direction = new THREE.Vector3().subVectors(
      this.ball.position,
      this.previousPosition
    );
    this.raycaster.ray.origin.copy(this.ballBody.position);
    this.raycaster.ray.direction.copy(direction); //new THREE.Vector3(0,0,-1));
    const intersectableObjects = this.scene.children.filter(
      (child) => child.userData.canCheckIntersects
    );
    const intersects = this.raycaster.intersectObjects(
      intersectableObjects,
      true
    );
    if (
      intersects.length > 0 &&
      Global.ballPos.z >=
        (Global.jumpHeight == 'low'
          ? -1.8
          : Global.jumpHeight == 'mid'
          ? -1.5
          : -1.4)
    ) {
      let topLevelParent = intersects[0].object;

      while (topLevelParent.parent && topLevelParent.parent !== this.scene) {
        topLevelParent = topLevelParent.parent;
      }
      if (topLevelParent.visible) {
        this.kickFinished = true;
        this.canCheckForGoal = false;
        this.canCheckRaycaster = false;
        this.events.dispatchEvent({
          type: 'SOUND_EVENTS',
          message: {
            event_type: 'play_sound',
            data: {
              sndKey: 'catch',
            },
          },
        });
        setTimeout(() => {
          this.events.dispatchEvent({
            type: 'SOUND_EVENTS',
            message: {
              event_type: 'play_sound',
              data: {
                sndKey: 'cheer',
              },
            },
          });
        }, 100);
        this.events.dispatchEvent({
          type: 'PLAYER_EVENTS',
          message: {
            event_type: 'defend',
            data: {
              itemType: topLevelParent.userData.itemType,
            },
          },
        });
        this.launchTwn && this.launchTwn.kill();

        this.ballBody.velocity.set(this.velIndex % 2 == 0 ? -5 : 5, 0, -5);
        this.ballBody.angularVelocity.set(
          -20,
          Math.random() * 10,
          Math.random() * 10
        );
      }
    }
    this.previousPosition.copy(this.ball.position);
  }

  launchBall(data) {
    this.kickFinished = false;
    this.canCheckForGoal = true;
    this.canCheckRaycaster = true;

    this.kickStarted = true;
    this.velIndex = Math.floor(Math.random() * (5 - 0) + 0);
    if (this.velIndex < 2) {
      Global.jumpHeight = 'high';
    } else if (this.velIndex < 4) {
      Global.jumpHeight = 'mid';
    } else {
      Global.jumpHeight = 'low';
    }

    const _velocity = this.ballVelocities[this.velIndex];

    this.kickCnt++;
    const _multiplier = (Math.random() * Math.min(30, this.kickCnt)) / 1000;
    this.ballBody.velocity.set(
      _velocity.x * (1 + _multiplier),
      _velocity.y,
      _velocity.z * (1 + _multiplier)
    );

    this.events.dispatchEvent({
      type: 'SOUND_EVENTS',
      message: {
        event_type: 'play_sound',
        data: {
          sndKey: 'kick',
        },
      },
    });

    return;
    this.events.dispatchEvent({
      type: 'SOUND_EVENTS',
      message: {
        event_type: 'play_sound',
        data: {
          sndKey: 'kick',
        },
      },
    });
    this.launchTwn = gsap.to(this.launchVal, {
      progress: 1.05,
      duration: duration,
      onUpdate: function () {
        if (this.launchVal.progress < 1) {
          this.ballBody.position.copy(
            this.getPositionAlongPath(this.launchVal.progress)
          );
        } else {
          this.launchTwn && this.launchTwn.kill();
          this.ballBody.velocity.set(0, 0, 0);
          // this.ballBody.angularVelocity.set(0, 0, -20);
        }
      }.bind(this),
      onComplete: function () {
        this.ballBody.angularVelocity.set(0, 0, 0);
      }.bind(this),
    });
    this.events.dispatchEvent({
      type: 'CAMERA_EVENTS',
      message: {
        event_type: 'camera_pan_start',
        data: {},
      },
    });
    setTimeout(() => {
      if (!this.kickFinished) {
        this.events.dispatchEvent({
          type: 'PLAYER_EVENTS',
          message: {
            event_type: 'retry',
            data: {},
          },
        });
      }
    }, 4000);
  }

  getPositionAlongPath(progress) {
    return Global.ballPath.getPointAt(progress);
  }

  dispose() {
    // Cleanup logic

    if (this.ball) {
      this.trail.dispose(this.scene);
      this.shadow.geometry.dispose();
      this.shadow.material.dispose();
      this.scene.remove(this.shadow);
      this.shadow = null;
      this.ball.traverse((child) => {
        if (child.isMesh) {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach((material) => material.dispose());
            } else {
              child.material.dispose();
            }
          }
        }
      });
      this.scene.remove(this.ball);
      this.ball = null;
    }
    this.events.removeEventListener('GAMESCENE_EVENTS', this.onEvents);
    this.events.removeEventListener('PLAYER_EVENTS', this.onEvents);
    this.events.removeEventListener('GAME_EVENTS', this.onEvents);
    // this.events=null;
  }
}
